import React, { useState } from "react";
import styled from "styled-components";
import { FiPlus, FiDollarSign } from "react-icons/fi";
import { CATEGORIES, COLORS, CURRENCIES } from "../utils/constants";
import { formatDateForInput } from "../utils/helpers";
import ReceiptUpload from "./ReceiptUpload";

const FormCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.gray200};
  border-radius: 12px;
  padding: 24px;
`;

const FormTitle = styled.h2`
  color: ${COLORS.gray800};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: ${(props) => props.$columns || "1fr"};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: ${COLORS.gray600};
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 14px;
  min-width: 0;
  padding: 10px 12px;
  transition: border-color 0.2s;
  width: 100%;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const Select = styled.select`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 14px;
  min-width: 0;
  padding: 10px 12px;
  transition: border-color 0.2s;
  width: 100%;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  min-height: 60px;
  padding: 10px 12px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const TypeToggle = styled.div`
  display: flex;
  gap: 0;
`;

const TypeButton = styled.button`
  background: ${(props) => (props.$active ? COLORS.primary : COLORS.white)};
  border: 1px solid
    ${(props) => (props.$active ? COLORS.primary : COLORS.gray300)};
  color: ${(props) => (props.$active ? COLORS.white : COLORS.gray600)};
  cursor: pointer;
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 16px;
  transition: all 0.2s;

  &:first-child {
    border-radius: 6px 0 0 6px;
  }

  &:last-child {
    border-radius: 0 6px 6px 0;
  }

  &:hover {
    background: ${(props) =>
      props.$active ? COLORS.primaryDark : COLORS.gray50};
  }
`;

const SubmitButton = styled.button`
  align-items: center;
  background: ${COLORS.primary};
  border: none;
  border-radius: 8px;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;
  justify-content: center;
  padding: 12px 24px;
  transition: background 0.2s;

  &:hover {
    background: ${COLORS.primaryDark};
  }

  &:disabled {
    background: ${COLORS.gray300};
    cursor: not-allowed;
  }
`;

const ExpenseForm = (props) => {
  const initialFormState = {
    type: "expense",
    amount: "",
    currency: props.defaultCurrency || "USD",
    category: "",
    payee: "",
    description: "",
    date: formatDateForInput(new Date()),
    receipts: [],
  };

  const [form, setForm] = useState(initialFormState);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    if (form.type === "expense" && !form.category) {
      alert("Please select a category.");
      return;
    }

    const transaction = {
      type: form.type,
      amount: Number(parseFloat(form.amount).toFixed(2)),
      currency: form.currency,
      category: form.type === "expense" ? form.category : "Fund Replenishment",
      payee: form.payee.trim(),
      description: form.description.trim(),
      date: form.date,
      receipts: form.receipts,
    };

    props.onAddTransaction(transaction);
    setForm(initialFormState);
  };

  return (
    <FormCard>
      <FormTitle>
        {form.type === "expense" ? "Record Expense" : "Add Funds"}
      </FormTitle>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Transaction Type</Label>
          <TypeToggle>
            <TypeButton
              type="button"
              $active={form.type === "expense"}
              onClick={() => updateField("type", "expense")}
            >
              Expense
            </TypeButton>
            <TypeButton
              type="button"
              $active={form.type === "replenish"}
              onClick={() => updateField("type", "replenish")}
            >
              Add Funds
            </TypeButton>
          </TypeToggle>
        </FormGroup>

        <FormRow $columns="1fr 1fr">
          <FormGroup>
            <Label>Amount *</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => updateField("amount", e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Currency *</Label>
            <Select
              value={form.currency}
              onChange={(e) => updateField("currency", e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} ({c.symbol}) - {c.name}
                </option>
              ))}
            </Select>
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Date *</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => updateField("date", e.target.value)}
            required
          />
        </FormGroup>

        {form.type === "expense" && (
          <FormRow $columns="1fr 1fr">
            <FormGroup>
              <Label>Category *</Label>
              <Select
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                required
              >
                <option value="">Select category...</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Payee / Vendor</Label>
              <Input
                type="text"
                placeholder="e.g. Staples, Uber"
                value={form.payee}
                onChange={(e) => updateField("payee", e.target.value)}
              />
            </FormGroup>
          </FormRow>
        )}

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            placeholder={
              form.type === "expense"
                ? "What was this expense for?"
                : "Reason for adding funds..."
            }
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormGroup>

        {form.type === "expense" && (
          <FormGroup>
            <Label>Receipt</Label>
            <ReceiptUpload
              receipts={form.receipts}
              onReceiptsChange={(receipts) => updateField("receipts", receipts)}
            />
          </FormGroup>
        )}

        <SubmitButton type="submit">
          {form.type === "expense" ? (
            <React.Fragment>
              <FiDollarSign size={18} /> Record Expense
            </React.Fragment>
          ) : (
            <React.Fragment>
              <FiPlus size={18} /> Add Funds
            </React.Fragment>
          )}
        </SubmitButton>
      </Form>
    </FormCard>
  );
};

export default ExpenseForm;
